import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { ArrowLeft, Send, Loader2, X, Mic, MessageSquareText, Heart, Briefcase, Users, UserRound } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { detectEmergingArchetype } from "@/lib/archetypes";
import {
  evolveTrajectory,
  INITIAL_TRAJECTORY_MEMORY,
  type TrajectoryMemory,
  type TrajectoryState,
} from "@/lib/behaviorRuntime";
import type { ForecastResponseMode } from "@/lib/forecastEngine";
import LiveTrajectoryPanel from "@/components/LiveTrajectoryPanel";
import { persistDebriefData } from "@/lib/debriefEngine";
import { getMessageDelay, splitForStaggeredDelivery, type TimingProfile } from "@/lib/messageTiming";
import {
  getConversationRhythm,
  applyRhythmToTiming,
  INITIAL_RHYTHM_MEMORY,
  type RhythmMemory,
  type RhythmResult,
} from "@/lib/rhythmEngine";

const sleep = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));

// Single source of truth for session length. Backend (rehearsal-turn) treats
// agentTurn >= MAX_AGENT_TURNS as complete; progress bar derives from it too.
const MAX_AGENT_TURNS = 10;

// ─── Conversational memory fragments ────────────────────────────
// Subtle continuity references the agent may reuse mid-simulation.
const MEMORY_FRAGMENTS = [
  "That's not what you said earlier.",
  "You keep changing the point.",
  "You already brought this up.",
  "Now you're acting different.",
  "You said the opposite a minute ago.",
  "This is exactly what you did last time.",
];

function pickMemoryFragment(turn: number, pressureTrend: string | undefined, volatility: number): string | null {
  if (turn < 4) return null;
  const heat = (pressureTrend === "rising" ? 0.35 : 0.18) + Math.min(0.25, volatility / 400);
  if (Math.random() > heat) return null;
  return MEMORY_FRAGMENTS[Math.floor(Math.random() * MEMORY_FRAGMENTS.length)];
}

type AgentActivity =
  | { kind: "idle" }
  | { kind: "silent"; label?: string }
  | { kind: "typing" }
  | { kind: "stopped" }
  | { kind: "resumed" }
  | { kind: "burst" };

const AgentActivityIndicator = ({ activity }: { activity: AgentActivity }) => {
  if (activity.kind === "idle") return null;

  const label =
    activity.kind === "silent"
      ? activity.label ?? "no reply yet"
      : activity.kind === "typing"
      ? "typing…"
      : activity.kind === "stopped"
      ? "paused"
      : activity.kind === "resumed"
      ? "typing again…"
      : "responding";


  const color =
    activity.kind === "silent"
      ? "text-muted-foreground/45"
      : activity.kind === "stopped"
      ? "text-amber-300/70"
      : activity.kind === "burst"
      ? "text-rose-300/85"
      : "text-cyan/65";

  const dotAnim =
    activity.kind === "stopped"
      ? "bg-amber-400/70"
      : activity.kind === "silent"
      ? "bg-muted-foreground/40"
      : "bg-cyan/70 animate-pulse";

  return (
    <div className="max-w-[80%] pl-1" style={{ animation: "fadeIn 0.35s ease-out" }}>
      <div className={`inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] ${color}`}>
        <span className={`w-1 h-1 rounded-full ${dotAnim}`} />
        <span>{label}</span>
      </div>
    </div>
  );
};

const MODE_OPTIONS: { key: ForecastResponseMode; label: string }[] = [
  { key: "soft_neutral", label: "Soft Neutral" },
  { key: "firm_boundary", label: "Firm Boundary" },
  { key: "minimal_grey_rock", label: "Grey Rock" },
];

type ScenarioKey = "relationship" | "workplace" | "family" | "friendship";

interface PreRehearsalState {
  entry_type: string;
  scenario?: ScenarioKey;
  patterns: { name: string; score?: number; count?: number }[];
  seed_context?: string;
}

const SCENARIO_PLACEHOLDERS: Record<ScenarioKey, string> = {
  relationship: "e.g. I need to bring up how they dismissed me last night",
  workplace: "e.g. they took credit for my work in the team meeting",
  family: "e.g. I want to set a boundary about them criticizing my choices",
  friendship: "e.g. they keep making plans and canceling on me",
};
const DEFAULT_PLACEHOLDER = "e.g. I need to confront them about canceling plans last minute";

const SCENARIO_TILES: { key: ScenarioKey; label: string; icon: typeof Heart }[] = [
  { key: "relationship", label: "Relationship", icon: Heart },
  { key: "workplace", label: "Workplace", icon: Briefcase },
  { key: "family", label: "Family", icon: Users },
  { key: "friendship", label: "Friendship", icon: UserRound },
];

interface ActiveSessionState {
  rehearsal_id: string;
  opening_message: string;
  agent_tactics: string[];
  entry_type: string;
}

interface ChatMessage {
  turn: number;
  role: "agent" | "user";
  content: string;
  tactic_tag: string | null;
}

const PROCESSING_PHRASES = [
  "Reading your reply…",
  "Thinking…",
  "Finding the words…",
  "Responding…",
];

const ProcessingIndicator = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % PROCESSING_PHRASES.length), 1400);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="max-w-[80%] pl-1">
      <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] text-cyan/60">
        <span className="w-1 h-1 rounded-full bg-cyan/70 animate-pulse" />
        <span key={idx} style={{ animation: "fadeIn 0.4s ease-out" }}>
          {PROCESSING_PHRASES[idx]}
        </span>
      </div>
    </div>
  );
};

const InitializingScreen = () => {
  const phrases = [
    "Setting up your practice space…",
    "Getting the other side ready…",
    "Almost ready…",
  ];

  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => Math.min(i + 1, phrases.length - 1)), 900);
    return () => clearInterval(t);
  }, []);
  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center"
      style={{
        background:
          "radial-gradient(60% 50% at 50% 40%, hsl(0 0% 8%), hsl(0 0% 3%))",
      }}
    >
      <div
        className="absolute inset-x-0 top-1/2 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent, hsl(184 100% 50% / 0.5), transparent)",
          boxShadow: "0 0 30px hsl(184 100% 50% / 0.4)",
          animation: "initSweep 2.4s ease-in-out infinite",
        }}
      />
      <div className="text-[10px] tracking-[0.4em] uppercase text-cyan/70 font-mono mb-6">
        Getting ready…
      </div>

      <div
        key={idx}
        className="text-xs tracking-[0.18em] uppercase text-muted-foreground/60 font-mono"
        style={{ animation: "fadeIn 0.5s ease-out" }}
      >
        {phrases[idx]}
      </div>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes initSweep {
          0%, 100% { transform: scaleX(0.2); opacity: 0.4; }
          50% { transform: scaleX(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

const SpeechRecognitionAPI = typeof window !== 'undefined'
  ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
  : null;

const RehearsalChat = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as (PreRehearsalState | ActiveSessionState) | null;

  // Context input phase
  const [phase, setPhase] = useState<"context" | "chat">("context");
  const [contextInput, setContextInput] = useState(
    state && 'patterns' in state ? (state as PreRehearsalState).seed_context || "" : ""
  );
  const [isBuilding, setIsBuilding] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [activeScenario, setActiveScenario] = useState<ScenarioKey>(
    (state && 'patterns' in state && (state as PreRehearsalState).scenario) || "relationship"
  );

  // Chat phase
  const [sessionData, setSessionData] = useState<ActiveSessionState | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentTurn, setCurrentTurn] = useState(1);
  const [isWaiting, setIsWaiting] = useState(false);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [turnError, setTurnError] = useState<{ userText: string; userTurn: number } | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Speech
  const [isListening, setIsListening] = useState(false);
  const isListeningRef = useRef(false);
  const [isContextListening, setIsContextListening] = useState(false);
  const recognitionRef = useRef<any>(null);
  const contextRecognitionRef = useRef<any>(null);
  const speechSupported = !!SpeechRecognitionAPI;

  // Explain tactic
  const [explainLoading, setExplainLoading] = useState(false);
  const [explainOverlay, setExplainOverlay] = useState<{ tactic: string; explanation: string } | null>(null);

  // Dynamic Behavioral Pressure runtime + Live Trajectory state
  const [responseMode, setResponseMode] = useState<ForecastResponseMode>("firm_boundary");
  const [trajectory, setTrajectory] = useState<TrajectoryState | null>(null);
  const trajectoryMemoryRef = useRef<TrajectoryMemory>(INITIAL_TRAJECTORY_MEMORY);
  const trajectoryHistoryRef = useRef<TrajectoryState[]>([]);
  const responseModeHistoryRef = useRef<ForecastResponseMode[]>([]);
  const [agentActivity, setAgentActivity] = useState<AgentActivity>({ kind: "idle" });
  const [rhythm, setRhythm] = useState<RhythmResult | null>(null);
  const rhythmMemoryRef = useRef<RhythmMemory>(INITIAL_RHYTHM_MEMORY);

  // Determine if we got pre-rehearsal data or an already-built session
  const isPreRehearsal = state && 'patterns' in state;
  const preData = isPreRehearsal ? (state as PreRehearsalState) : null;

  // If state already has rehearsal_id (legacy flow), skip context phase
  useEffect(() => {
    if (state && 'rehearsal_id' in state && (state as ActiveSessionState).rehearsal_id) {
      const s = state as ActiveSessionState;
      setSessionData(s);
      setMessages([
        { turn: 1, role: "agent", content: s.opening_message, tactic_tag: s.agent_tactics?.[0] || "Unknown" },
      ]);
      setPhase("chat");
    }
  }, []);

  // Cleanup speech on unmount
  useEffect(() => {
    return () => {
      recognitionRef.current?.stop();
      contextRecognitionRef.current?.stop();
    };
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isWaiting, agentActivity]);

  // Auto-transition cinematically into the debrief once the session completes.
  // We never reset the simulator — we always carry the user forward into reflection.
  const [completionPhase, setCompletionPhase] = useState<"idle" | "settling" | "complete" | "transitioning">("idle");
  const goToDebrief = useCallback(() => {
    const rehearsalId = sessionData?.rehearsal_id;
    if (rehearsalId) {
      persistDebriefData(rehearsalId, {
        trajectoryHistory: trajectoryHistoryRef.current,
        responseModeHistory: responseModeHistoryRef.current,
      });
      navigate(`/rehearsal/debrief/${rehearsalId}`, { replace: true });
      return;
    }
    navigate("/rehearsal/debrief", { replace: true });
  }, [navigate, sessionData?.rehearsal_id]);

  useEffect(() => {
    if (!sessionComplete || !sessionData?.rehearsal_id) return;
    setCompletionPhase("settling");
    const t1 = window.setTimeout(() => setCompletionPhase("complete"), 1400);
    const t2 = window.setTimeout(() => setCompletionPhase("transitioning"), 4200);
    const t3 = window.setTimeout(goToDebrief, 5400);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
  }, [sessionComplete, goToDebrief, sessionData?.rehearsal_id]);

  const toggleListening = useCallback(() => {
    if (!SpeechRecognitionAPI) return;
    
    // If currently listening, stop
    if (isListeningRef.current && recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (_) {}
      recognitionRef.current = null;
      isListeningRef.current = false;
      setIsListening(false);
      return;
    }
    
    // Always destroy any stale instance before creating a new one
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (_) {}
      recognitionRef.current = null;
    }
    
    const recognition = new SpeechRecognitionAPI();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.continuous = false;
    recognitionRef.current = recognition;
    
    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setInputValue(prev => prev ? prev + ' ' + transcript : transcript);
    };
    recognition.onend = () => {
      recognitionRef.current = null;
      isListeningRef.current = false;
      setIsListening(false);
    };
    recognition.onerror = () => {
      recognitionRef.current = null;
      isListeningRef.current = false;
      setIsListening(false);
    };
    
    recognition.start();
    isListeningRef.current = true;
    setIsListening(true);
  }, []);

  if (!state) {
    return <Navigate to="/analysis-result" replace />;
  }

  const primaryTactic = sessionData?.agent_tactics?.[0] || preData?.patterns?.[0]?.name || "Unknown";

  const toggleContextListening = () => {
    if (!SpeechRecognitionAPI) return;
    if (isContextListening && contextRecognitionRef.current) {
      contextRecognitionRef.current.stop();
      setIsContextListening(false);
      return;
    }
    const recognition = new SpeechRecognitionAPI();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.continuous = false;
    contextRecognitionRef.current = recognition;
    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setContextInput(prev => prev ? prev + ' ' + transcript : transcript);
    };
    recognition.onend = () => setIsContextListening(false);
    recognition.onerror = () => setIsContextListening(false);
    recognition.start();
    setIsContextListening(true);
  };



  const handleStartSimulation = async () => {
    if (!preData || isBuilding) return;
    setIsBuilding(true);

    try {
      const resp = await supabase.functions.invoke("build-rehearsal-agent", {
        body: {
          entry_type: preData.entry_type,
          scenario: activeScenario,
          source_session_id: null,
          forensic_baseline: {
            entry_type: preData.entry_type,
            patterns: preData.patterns.slice(0, 3),
          },
          user_context: contextInput.trim() || undefined,
        },
      });

      if (resp.error) {
        const errorData = resp.data;
        if (errorData?.error === "insufficient_credits") {
          toast({
            title: "Insufficient credits",
            description: `You need 2 credits to rehearse. You have ${errorData.credits_remaining}.`,
            variant: "destructive",
          });
          setIsBuilding(false);
          return;
        }
        if (errorData?.error === "rehearsal_start_failed") {
          toast({
            title: "Couldn't start rehearsal",
            description: errorData.refunded
              ? "Your 2 credits were refunded. Please try again."
              : "Please try again in a moment.",
            variant: "destructive",
          });
          setIsBuilding(false);
          return;
        }
        throw resp.error;
      }

      const result = resp.data;
      const active: ActiveSessionState = {
        rehearsal_id: result.rehearsal_id,
        opening_message: result.opening_message,
        agent_tactics: result.agent_tactics,
        entry_type: result.entry_type,
      };
      setSessionData(active);
      setMessages([
        { turn: 1, role: "agent", content: result.opening_message, tactic_tag: result.agent_tactics?.[0] || "Unknown" },
      ]);
      setPhase("chat");
    } catch (err) {
      console.error(err);
      toast({ title: "Could not start rehearsal", description: "Please try again.", variant: "destructive" });
    } finally {
      setIsBuilding(false);
    }
  };

  const runAgentTurn = async (userText: string, userTurn: number, isRetry: boolean) => {
    if (!sessionData) return;
    setIsWaiting(true);
    setTurnError(null);

    // Evolve the live behavioral trajectory (stateful, multi-turn)
    const tacticForRuntime = sessionData.agent_tactics?.[0] || "generic";
    const { state: nextTrajectory, memory: nextMemory } = evolveTrajectory(
      trajectoryMemoryRef.current,
      {
        tactic: tacticForRuntime,
        responseMode,
        lastUserReply: userText,
      },
    );
    trajectoryMemoryRef.current = nextMemory;
    setTrajectory(nextTrajectory);
    trajectoryHistoryRef.current = [...trajectoryHistoryRef.current, nextTrajectory];
    responseModeHistoryRef.current = [...responseModeHistoryRef.current, responseMode];

    // Compute adaptive timing profile for THIS agent turn (silence / typing / fragment cadence)
    const baseTiming: TimingProfile = getMessageDelay({
      pressureState: nextTrajectory.pressureState,
      pressureLevel: nextTrajectory.nextPressureLevel,
      volatility: nextTrajectory.emotionalVolatility,
      withdrawalLevel: nextTrajectory.withdrawalWeight,
      nextTurnStyle: nextTrajectory.nextTurnStyle,
      turn: userTurn,
    });

    // Conversational Rhythm Intelligence — classify cadence + apply compression
    const rhythmResult = getConversationRhythm({
      trajectoryState: nextTrajectory,
      pressureLevel: nextTrajectory.nextPressureLevel,
      volatility: nextTrajectory.emotionalVolatility,
      withdrawalLevel: nextTrajectory.withdrawalWeight,
      nextTurnStyle: nextTrajectory.nextTurnStyle,
      responseMode,
      memory: rhythmMemoryRef.current,
    });
    rhythmMemoryRef.current = rhythmResult.memory;
    setRhythm(rhythmResult);
    const timing = applyRhythmToTiming(baseTiming, rhythmResult);

    // Pull a memory fragment hint occasionally — backend may weave it in.
    const memoryFragmentHint = pickMemoryFragment(
      userTurn,
      nextTrajectory.pressureTrend,
      nextTrajectory.emotionalVolatility,
    );

    // Fire request immediately; run timing animation in parallel.
    const requestPromise = supabase.functions.invoke("rehearsal-turn", {
      body: {
        rehearsal_id: sessionData.rehearsal_id,
        user_message: userText,
        turn_number: userTurn,
        response_mode: responseMode,
        runtime: {
          nextTurnStyle: nextTrajectory.nextTurnStyle,
          pressureState: nextTrajectory.pressureState,
          escalationWeight: nextTrajectory.escalationWeight,
          withdrawalWeight: nextTrajectory.withdrawalWeight,
          memoryFragmentHint,
          rhythm: {
            type: rhythmResult.rhythmType,
            tempo: rhythmResult.pacingTempo,
            cadence: rhythmResult.cadencePattern,
            compressionFactor: rhythmResult.compressionFactor,
            fragmentationLevel: rhythmResult.fragmentationLevel,
            silenceDrift: rhythmResult.silenceDrift,
          },
        },
      },
    });

    try {
      // PHASE 1 — silence / cold gap
      setAgentActivity({ kind: "silent", label: timing.silenceLabel });
      await sleep(timing.responseDelayMs);

      // PHASE 2 — typing, possibly with hesitation
      setAgentActivity({ kind: "typing" });
      if (Math.random() < timing.hesitationProbability) {
        await sleep(Math.round(timing.typingDelayMs * 0.4));
        setAgentActivity({ kind: "stopped" });
        await sleep(650);
        setAgentActivity({ kind: "resumed" });
        await sleep(Math.round(timing.typingDelayMs * 0.6));
      } else {
        await sleep(timing.typingDelayMs);
      }

      const resp = await requestPromise;
      if (resp.error) throw resp.error;
      const data = resp.data;

      const fullText: string = data.agent_message || "";
      const fragments = timing.fragmented ? splitForStaggeredDelivery(fullText) : [fullText];
      const baseTurn = data.turn_number;

      for (let i = 0; i < fragments.length; i++) {
        const frag = fragments[i];
        setMessages((prev) => [
          ...prev,
          {
            turn: baseTurn + i * 0.001,
            role: "agent",
            content: frag,
            tactic_tag: i === 0 ? data.tactic_used : null,
          },
        ]);
        if (i < fragments.length - 1) {
          setAgentActivity({ kind: "typing" });
          await sleep(timing.fragmentGapMs);
        }
      }

      setCurrentTurn(baseTurn);
      if (data.status === "session_complete" || data.session_complete || baseTurn >= MAX_AGENT_TURNS) {
        setSessionComplete(true);
      }
    } catch (err) {
      console.error(err);
      // Surface explicit retry state so the user knows the agent did NOT reply.
      setTurnError({ userText, userTurn });
      toast({
        title: "Reply didn't come through",
        description: "The agent didn't respond. Tap retry to resend.",
        variant: "destructive",
      });
    } finally {
      setAgentActivity({ kind: "idle" });
      setIsWaiting(false);
    }
  };

  const handleSend = async () => {
    if (!inputValue.trim() || isWaiting || sessionComplete || !sessionData) return;

    const userText = inputValue.trim();
    const userTurn = currentTurn + 1;

    const userMsg: ChatMessage = {
      turn: userTurn,
      role: "user",
      content: userText,
      tactic_tag: null,
    };
    setMessages((prev) => [...prev, userMsg]);
    setInputValue("");

    await runAgentTurn(userText, userTurn, false);
  };

  const handleRetryFailedTurn = async () => {
    if (!turnError || isWaiting) return;
    await runAgentTurn(turnError.userText, turnError.userTurn, true);
  };

  const handleExplainTactic = async () => {
    if (explainLoading || explainOverlay) return;

    const lastAgent = [...messages].reverse().find((m) => m.role === "agent");
    if (!lastAgent) return;

    const tacticTag = lastAgent.tactic_tag || primaryTactic;
    setExplainLoading(true);

    try {
      const resp = await supabase.functions.invoke("explain-tactic", {
        body: { tactic_tag: tacticTag, agent_message: lastAgent.content },
      });

      if (resp.error) throw resp.error;
      setExplainOverlay({ tactic: tacticTag, explanation: resp.data.explanation });
    } catch (err) {
      console.error(err);
      toast({ title: "Could not explain tactic", description: "Try again.", variant: "destructive" });
    } finally {
      setExplainLoading(false);
    }
  };

  // ─── CONTEXT SCREEN ───
  if (phase === "context") {
    if (isBuilding) return <InitializingScreen />;
    return (
      <div className="flex flex-col h-screen bg-background">
        {/* HEADER */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
          <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Badge variant="outline" className="text-xs font-medium border-border text-muted-foreground">
            {primaryTactic} Archetype
          </Badge>
          <div className="w-5" />
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-6 space-y-6 max-w-lg mx-auto w-full">
          <div className="text-center space-y-2">
            <MessageSquareText className="w-10 h-10 text-primary mx-auto mb-3" />
            <h2 className="text-xl font-bold text-foreground">Set the Scene</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Describe the situation you want to rehearse. The simulated agent will open with a message relevant to your scenario.
            </p>
          </div>

          <div className="w-full space-y-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground/70 font-mono mb-3 text-center">
                Scenario
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {SCENARIO_TILES.map(({ key, label, icon: Icon }) => {
                  const active = activeScenario === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setActiveScenario(key)}
                      disabled={isBuilding}
                      className={cn(
                        "flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all",
                        active
                          ? "border-cyan/40 bg-cyan/10 text-cyan shadow-[0_0_20px_-8px_hsl(180_100%_50%_/_0.5)]"
                          : "border-border/50 bg-secondary/30 text-muted-foreground hover:text-foreground/90 hover:border-border"
                      )}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="relative">
              <Textarea
                value={contextInput}
                onChange={(e) => setContextInput(e.target.value)}
                placeholder={SCENARIO_PLACEHOLDERS[activeScenario] ?? DEFAULT_PLACEHOLDER}
                className="min-h-[100px] pr-10 bg-secondary border-border text-foreground placeholder:text-muted-foreground resize-none rounded-xl"
                disabled={isBuilding}
              />
              {speechSupported && (
                <button
                  type="button"
                  onClick={toggleContextListening}
                  disabled={isBuilding}
                  className={`absolute right-3 top-3 p-1 rounded-lg transition-all disabled:opacity-50 ${
                    isContextListening
                      ? "text-cyan animate-pulse-recording"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  title={isContextListening ? "Stop listening" : "Dictate"}
                >
                  <Mic className="w-4 h-4" />
                </button>
              )}
            </div>

            <p className="text-[11px] text-muted-foreground text-center">
              Optional — leave blank and the agent will open with a generic approach
            </p>
          </div>

          <Button
            onClick={handleStartSimulation}
            disabled={isBuilding}
            className="w-full rounded-xl"
            size="lg"
          >
            {isBuilding ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" />Building simulation...</>
            ) : (
              "Start Simulation"
            )}
          </Button>
        </div>
      </div>
    );
  }

  // ─── CHAT SCREEN ───
  const firstAgentIdx = messages.findIndex((m) => m.role === "agent");
  const firstUserIdx = messages.findIndex((m) => m.role === "user");

  const atmosphereBg =
    trajectory?.atmosphere === "volatile"
      ? "radial-gradient(70% 60% at 50% 0%, hsl(0 35% 9%), hsl(0 0% 4%) 70%)"
      : trajectory?.atmosphere === "tense"
      ? "radial-gradient(70% 60% at 50% 0%, hsl(38 25% 8%), hsl(0 0% 4%) 70%)"
      : trajectory?.atmosphere === "withdrawn"
      ? "radial-gradient(70% 60% at 50% 0%, hsl(220 8% 7%), hsl(0 0% 3%) 70%)"
      : "radial-gradient(70% 60% at 50% 0%, hsl(0 0% 9%), hsl(0 0% 4%) 70%)";

  return (
    <div
      className="flex flex-col h-screen relative transition-[background] duration-1000"
      style={{ background: atmosphereBg }}
    >
      {/* Ambient scanlines / depth */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, hsl(184 100% 50% / 0.4) 0 1px, transparent 1px 3px)",
        }}
      />

      {/* SIMULATION STATUS — calm tactical */}
      <div className="w-full bg-cyan/[0.04] border-b border-cyan/15 px-4 py-2 text-center shrink-0 backdrop-blur-sm">
        <div className="inline-flex items-center gap-2">
          <span
            className={`w-1.5 h-1.5 rounded-full ${
              trajectory?.atmosphere === "volatile"
                ? "bg-rose-400 animate-pulse"
                : "bg-cyan animate-pulse"
            }`}
          />
          <p className="text-[10px] font-mono tracking-[0.28em] uppercase text-cyan/80">
            Practice in progress
          </p>
        </div>
        <p className="text-[10px] text-muted-foreground/50 mt-0.5 tracking-wide">
          This is a safe space to practice — nothing here is real.
        </p>

      </div>

      {/* AMBIENT ARCHETYPE OBSERVATION — subtle, low-interruption */}
      {(() => {
        const emerging = detectEmergingArchetype(messages);
        if (!emerging) return null;
        return (
          <div
            key={`emerging-${emerging.turn}`}
            className="w-full px-4 py-1.5 text-center shrink-0 border-b border-cyan/10"
            style={{ animation: "fadeIn 0.6s ease-out" }}
          >
            <p className="text-[10px] font-mono tracking-[0.24em] uppercase text-cyan/55">
              Starting to see a {emerging.archetype.name.replace(/^The /, "")} pattern
            </p>

          </div>
        );
      })()}

      {/* HEADER ROW */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/40 shrink-0">
        <button onClick={() => navigate(-1)} className="text-muted-foreground/70 hover:text-foreground transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <Badge variant="outline" className="text-[10px] font-mono tracking-[0.18em] uppercase border-border/50 text-muted-foreground/80">
          {primaryTactic}
        </Badge>
        <div className="w-5" />
      </div>

      {/* PROGRESS BAR */}
      <div className="w-full h-[2px] bg-muted/40 shrink-0">
        <div
          className="h-full bg-cyan/70 transition-all duration-500 ease-in-out"
          style={{
            width: `${Math.min((Math.ceil(currentTurn / 2) / MAX_AGENT_TURNS) * 100, 100)}%`,
            boxShadow: "0 0 8px hsl(184 100% 50% / 0.5)",
          }}
        />
      </div>

      {/* CHAT AREA */}
      <div className="flex-1 overflow-y-auto px-4 py-8 space-y-5 max-w-2xl w-full mx-auto relative">
        {/* LIVE TRAJECTORY — forensic intelligence overlay */}
        <div className="hidden md:block absolute top-3 right-3 w-[230px] z-10 pointer-events-none">
          <div className="pointer-events-auto">
            <LiveTrajectoryPanel state={trajectory} rhythm={rhythm} />
          </div>
        </div>
        <div className="md:hidden -mt-4 mb-2">
          <LiveTrajectoryPanel state={trajectory} rhythm={rhythm} />
        </div>
        {messages.map((msg, idx) => {
          if (msg.role === "agent") {
            const showLabel = idx === firstAgentIdx;
            return (
              <div
                key={`${msg.turn}-${msg.role}`}
                className="max-w-[82%]"
                style={{ animation: "msgFadeIn 0.5s ease-out" }}
              >
                {showLabel && (
                  <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-muted-foreground/60 mb-2">
                    Them · Simulated
                  </p>
                )}
                <div className="bg-card/60 border border-border/40 rounded-2xl rounded-tl-sm px-5 py-4 backdrop-blur-sm">
                  <p className="text-[15px] text-foreground/95 leading-[1.65]">{msg.content}</p>
                </div>
              </div>
            );
          } else {
            const showLabel = idx === firstUserIdx;
            return (
              <div
                key={`${msg.turn}-${msg.role}`}
                className="flex justify-end"
                style={{ animation: "msgFadeIn 0.4s ease-out" }}
              >
                <div className="max-w-[82%]">
                  {showLabel && (
                    <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-muted-foreground/60 mb-2 text-right">
                      You
                    </p>
                  )}
                  <div className="bg-cyan/[0.08] border border-cyan/20 rounded-2xl rounded-tr-sm px-5 py-4">
                    <p className="text-[15px] text-foreground/95 leading-[1.65]">{msg.content}</p>
                  </div>
                </div>
              </div>
            );
          }
        })}
        {agentActivity.kind !== "idle" && <AgentActivityIndicator activity={agentActivity} />}
        {turnError && !isWaiting && (
          <div
            className="max-w-[82%] rounded-2xl border border-rose-400/30 bg-rose-500/[0.06] px-4 py-3 space-y-2"
            style={{ animation: "msgFadeIn 0.4s ease-out" }}
          >
            <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-rose-300/85">
              No reply — agent didn't respond
            </p>
            <p className="text-xs text-muted-foreground/80 leading-relaxed">
              The connection dropped before the agent answered your last message. Your turn was not lost.
            </p>
            <div className="flex gap-2 pt-1">
              <Button
                size="sm"
                variant="outline"
                onClick={handleRetryFailedTurn}
                className="rounded-lg border-rose-400/40 text-rose-100 hover:bg-rose-500/10"
              >
                Retry that turn
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setTurnError(null)}
                className="rounded-lg text-muted-foreground hover:text-foreground"
              >
                Dismiss
              </Button>
            </div>
          </div>
        )}
        <div ref={chatEndRef} />

      </div>
      <style>{`
        @keyframes msgFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* INPUT AREA */}
      <div className="border-t border-border/40 px-4 py-4 shrink-0 space-y-3 max-w-2xl w-full mx-auto">
        {sessionComplete ? (
          <button
            onClick={goToDebrief}
            className="w-full text-center text-[10px] font-mono tracking-[0.32em] uppercase text-cyan/70 hover:text-cyan transition-colors py-3"
          >
            Continue to Debrief
          </button>
        ) : (
          <>
            {/* RESPONSE POSTURE — drives runtime adaptation */}
            <div className="flex items-center justify-between gap-2">
              <span className="text-[9px] font-mono tracking-[0.24em] uppercase text-muted-foreground/50">
                Your approach
              </span>

              <div className="inline-flex p-0.5 rounded-lg bg-background/60 border border-border/60">
                {MODE_OPTIONS.map((m) => {
                  const active = responseMode === m.key;
                  return (
                    <button
                      key={m.key}
                      type="button"
                      onClick={() => setResponseMode(m.key)}
                      disabled={isWaiting}
                      className={`px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-[0.16em] transition-all disabled:opacity-50 ${
                        active
                          ? "bg-cyan/10 text-cyan shadow-inner"
                          : "text-muted-foreground/55 hover:text-foreground/80"
                      }`}
                    >
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex gap-2">
              <div className="relative flex-1 group">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSend()}
                  placeholder="Compose your response…"
                  disabled={isWaiting}
                  className="w-full h-12 rounded-xl bg-secondary/40 border border-border/50 px-4 pr-10 text-[15px] text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-cyan/50 focus:bg-secondary/60 focus:shadow-[0_0_24px_-8px_hsl(184_100%_50%_/_0.5)] transition-all disabled:opacity-50"
                />
                {speechSupported && (
                  <button
                    type="button"
                    onClick={toggleListening}
                    disabled={isWaiting}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg transition-all disabled:opacity-50 ${
                      isListening
                        ? "text-cyan animate-pulse-recording"
                        : "text-muted-foreground/60 hover:text-foreground"
                    }`}
                    title={isListening ? "Stop listening" : "Dictate"}
                  >
                    <Mic className="w-4 h-4" />
                  </button>
                )}
              </div>
              <Button
                onClick={handleSend}
                disabled={!inputValue.trim() || isWaiting}
                size="default"
                className="rounded-xl h-12 px-5 bg-cyan/90 hover:bg-cyan text-primary-foreground"
              >
                {isWaiting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
            <div className="flex items-center justify-between px-1">
              <button
                onClick={handleExplainTactic}
                disabled={explainLoading}
                className="text-[10px] font-mono tracking-[0.18em] uppercase text-muted-foreground/60 hover:text-cyan transition-colors disabled:opacity-50"
              >
                {explainLoading ? "Analyzing…" : "What just happened?"}
              </button>
              <button
                onClick={() => {
                  setIsExiting(true);
                  window.setTimeout(goToDebrief, 1100);
                }}
                className="text-[10px] font-mono tracking-[0.18em] uppercase text-muted-foreground/60 hover:text-foreground/90 transition-colors"
              >
                End Session
              </button>
            </div>
          </>
        )}
      </div>

      {/* SESSION COMPLETE — cinematic closure overlay (auto-transitions to debrief) */}
      {completionPhase !== "idle" && (
        <div
          className="fixed inset-0 z-[70] flex flex-col items-center justify-center px-6"
          style={{
            background:
              "radial-gradient(60% 50% at 50% 50%, hsl(0 0% 6% / 0.96), hsl(0 0% 2% / 1))",
            animation: "fadeIn 0.8s ease-out forwards",
          }}
        >
          {/* ambient cyan sweep */}
          <div
            aria-hidden
            className="absolute inset-x-0 top-1/2 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent, hsl(184 100% 50% / 0.45), transparent)",
              boxShadow: "0 0 40px hsl(184 100% 50% / 0.35)",
              animation: "initSweep 3.2s ease-in-out infinite",
            }}
          />

          {completionPhase === "settling" && (
            <div
              className="text-[10px] tracking-[0.4em] uppercase text-muted-foreground/55 font-mono"
              style={{ animation: "fadeIn 0.6s ease-out" }}
            >
              Last message in
            </div>
          )}

          {(completionPhase === "complete" || completionPhase === "transitioning") && (
            <div
              className="flex flex-col items-center gap-5 text-center"
              style={{ animation: "fadeIn 0.9s ease-out" }}
            >
              <div className="text-[10px] tracking-[0.42em] uppercase text-cyan/75 font-mono">
                Nice work
              </div>
              <div className="h-px w-20 bg-cyan/40" style={{ boxShadow: "0 0 14px hsl(184 100% 50% / 0.5)" }} />
              <p className="text-sm text-foreground/75 font-light tracking-wide max-w-sm leading-relaxed">
                Putting together what you did well and what to watch for.
              </p>
            </div>
          )}

          {completionPhase === "transitioning" && (
            <div
              className="absolute bottom-16 text-[9px] font-mono tracking-[0.36em] uppercase text-muted-foreground/40"
              style={{ animation: "fadeIn 0.5s ease-out" }}
            >
              Here's your recap…
            </div>
          )}

          {/* Skip affordance — lets the user jump straight to debrief */}
          <button
            onClick={goToDebrief}
            className="absolute bottom-6 right-6 text-[10px] font-mono tracking-[0.28em] uppercase text-muted-foreground/50 hover:text-cyan transition-colors"
          >
            Skip →
          </button>

          <style>{`
            @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes initSweep {
              0%, 100% { transform: scaleX(0.2); opacity: 0.3; }
              50% { transform: scaleX(1); opacity: 1; }
            }
          `}</style>
        </div>
      )}


      {/* SOFT EXIT OVERLAY */}
      {isExiting && (
        <div
          className="fixed inset-0 z-[60] flex flex-col items-center justify-center"
          style={{
            background:
              "radial-gradient(60% 50% at 50% 50%, hsl(0 0% 5% / 0.95), hsl(0 0% 2% / 1))",
            animation: "fadeIn 0.5s ease-out forwards",
          }}
        >
          <div className="text-[10px] tracking-[0.4em] uppercase text-cyan/70 font-mono mb-3">
            Wrapping up
          </div>
          <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground/60 font-mono">
            Putting your recap together…
          </div>
          <button
            onClick={goToDebrief}
            className="absolute bottom-6 right-6 text-[10px] font-mono tracking-[0.28em] uppercase text-muted-foreground/50 hover:text-cyan transition-colors"
          >
            Skip →
          </button>
        </div>
      )}


      {/* EXPLAIN TACTIC OVERLAY */}
      {explainOverlay && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/85">
          <div className="w-[85%] max-w-[400px] bg-card border border-border rounded-2xl p-6 space-y-5 text-center animate-in zoom-in-95 fade-in duration-200">
            <div className="flex justify-center">
              <Badge variant="outline" className="text-xs font-medium border-border text-muted-foreground">
                {explainOverlay.tactic}
              </Badge>
            </div>
            <p className="text-sm text-foreground leading-relaxed px-2">{explainOverlay.explanation}</p>
            <Button className="w-full rounded-xl" onClick={() => setExplainOverlay(null)}>
              Got it
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RehearsalChat;
